import React from 'react';
import { usePermissions } from '../../contexts/PermissionsContext';

/**
 * مكون يتحكم في عرض المحتوى بناءً على صلاحيات المستخدم
 * @param {Object} props
 * @param {string|string[]} props.permission - الصلاحية المطلوبة أو مصفوفة الصلاحيات
 * @param {boolean} props.requireAll - هل يجب توفر كافة الصلاحيات (true) أم يكفي واحدة منها (false) - افتراضياً false
 * @param {React.ReactNode} props.children - المحتوى الذي سيتم عرضه إذا كان المستخدم يملك الصلاحية
 * @param {React.ReactNode} props.fallback - المحتوى البديل الذي سيتم عرضه إذا لم يكن المستخدم يملك الصلاحية
 * @returns {JSX.Element|null}
 */
const PermissionGuard = ({ 
  permission, 
  permissions, // للتوافق مع الكود القديم
  requireAll = false, 
  children, 
  fallback = null
}) => {
  // نستخدم نظام الصلاحيات الأصلي
  const { hasPermission, getUserPermissions } = usePermissions();
  
  const [isVisible, setIsVisible] = React.useState(false);
  
  // التحقق من الصلاحيات عند تحميل المكون وعند تغيير أي بيانات
  React.useEffect(() => {
    // تحديد الصلاحية المطلوبة (دعم الكود القديم باستخدام permissions أو الجديد باستخدام permission)
    const requiredPermission = permission || permissions;
    
    // التحقق المباشر من الصلاحيات في كل مرة يتم فيها تحديث المكون
    const checkPermissions = () => {
      // إذا لم يتم تحديد صلاحيات، نعرض المحتوى مباشرة
      if (!requiredPermission || (Array.isArray(requiredPermission) && requiredPermission.length === 0)) {
        setIsVisible(true);
        return;
      }
      
      // تحويل الصلاحية إلى مصفوفة إذا لم تكن كذلك
      const permissionArray = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
      
      // التحقق من الصلاحيات بشكل فوري
      const hasRequiredPermissions = requireAll
        ? permissionArray.every(perm => hasPermission(perm))
        : permissionArray.some(perm => hasPermission(perm));
      
      // تحديث حالة الرؤية
      setIsVisible(hasRequiredPermissions);
    };
    
    // تنفيذ التحقق الأولي
    checkPermissions();
    
    // الاستماع للتغييرات في الصلاحيات
    window.addEventListener('storage', checkPermissions);
    window.addEventListener('permissionsChanged', checkPermissions);
    
    return () => {
      // تنظيف المستمعين عند إزالة المكون
      window.removeEventListener('storage', checkPermissions);
      window.removeEventListener('permissionsChanged', checkPermissions);
    };
    
  }, [permission, permissions, requireAll, hasPermission]);
  
  // إذا كان العنصر مخفياً، نرجع null (DOM لا شيء)
  if (!isVisible) {
    return null;
  }
  
  // إذا كان العنصر مرئياً، نعرض المحتوى
  return children;
};

export default PermissionGuard;
